import React, { useState } from 'react';

const IECCodeRegistration = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: 'I need help with IEC Code registration.',
    website_hp: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  try {
    const response = await fetch("https://akiradvisory.in/send_mail.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      setFormStatus('success');
      // Reset form while keeping the default message
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: 'I need help with IEC Code registration.',
        website_hp: ''
      });
    } else {
      setFormStatus('error');
    }
  } catch (error) {
    setFormStatus('error');
  } finally {
    setIsSubmitting(false);
    setTimeout(() => setFormStatus(''), 5000);
  }
};

  return (
    <main>
      {/* Hero Section */}
      <section className="relative bg-blue-700 pt-16 pb-12">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:w-2/3">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              IEC Code Registration
            </h1>
            <p className="mt-4 text-xl text-blue-100">
              From eligibility assessment to digital certificate delivery, we manage your entire Import Export Code (IEC) registration—ensuring legal compliance, customs clearance, and access to global trade benefits for your business.
            </p>
            <div className="mt-8">
              <a href="#contact-section" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                  <span className="mr-2">📈</span> Empowering Your IEC Journey.
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Navigation */}
      <section className="bg-gray-50 border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex space-x-6 overflow-x-auto text-sm font-medium">
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#details">Overview & Benefits</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#procedure">Registration Process</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#documents">Documents Required</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#you-get">What You Get</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#service-faqs">FAQs</a></li>
            </ul>
        </div>
      </section>

      {/* Overview Section */}
      <section id="details" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 border-b pb-2">
                Overview of IEC Code Registration
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Importer Exporter Code (IEC) is a mandatory 10-digit business identification number issued by the Directorate General of Foreign Trade (DGFT) to businesses engaged in import and export activities in India. It is essential to legally clear shipments through customs and facilitate international trade operations seamlessly. IEC registration is required by all entities involved in cross-border trade, including proprietorships, partnerships, LLPs, companies, trusts, and societies.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                IEC simplifies international trade by providing unique identification and enables businesses to access various benefits such as export incentives, customs clearance, and trade finance. The IEC registration process is integrated online with the DGFT portal, making application and renewal straightforward and efficient.
              </p>

              <div className="mt-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">
                  Key Features & Benefits
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Mandatory for Import and Export:</strong> No shipment or customs clearance can be processed without an active IEC.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Lifetime Validity:</strong> Once issued, IEC is valid for life with mandatory annual profile updates to remain active.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Facilitates International Payments:</strong> Banks require IEC to process foreign currency payments related to imports and exports.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Access to Export Benefits:</strong> Enables availing government export incentives, duty drawbacks, and subsidies.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Easy Compliance:</strong> Online application, payment, and tracking streamline IEC acquisition and management.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Applicable to Various Entities:</strong> Suitable for individuals, SMEs, large corporations, NGOs, and startups engaged in trade.
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-1" id="contact-section">
              <div className="bg-gray-50 p-6 rounded-xl shadow-xl border-t-4 border-blue-600 sticky top-28">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Quick Enquiry
                </h3>
                
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-100 text-green-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">✅</span>
                    Thank you! Your enquiry has been submitted. We'll contact you shortly.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-100 text-red-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">❌</span>
                    There was an error submitting your form. Please try again.
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleFormSubmit}>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Full Name" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <textarea 
                    rows="3" 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="I need help with IEC Code registration." 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required
                  ></textarea>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3 inline-block"></div>
                        Submitting...
                      </>
                    ) : (
                      'Request an Initial Consultation'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Procedure Section */}
      <section id="procedure" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Our Comprehensive Process
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              8 Steps for IEC Code Registration
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 grid-flow-row-dense">
            {[
              { number: "01", icon: "📊", title: "Consultation and Eligibility Check", desc: "Understand your business type and trade requirements to guide IEC application." },
              { number: "02", icon: "📋", title: "Document Collection", desc: "Gather necessary documents like PAN, business proof, and bank details." },
              { number: "03", icon: "💻", title: "Online Application", desc: "Submit IEC application on DGFT portal with all required details and digital signature/Aadhaar authentication." },
              { number: "04", icon: "💳", title: "Payment of Fees", desc: "Complete fee payment through online gateway within the application portal." },
              { number: "05", icon: "📱", title: "Application Tracking", desc: "Monitor application status and clear any queries raised by DGFT officials." },
              { number: "06", icon: "✅", title: "IEC Issue", desc: "Receive IEC certificate digitally via email upon approval." },
              { number: "07", icon: "🔄", title: "Profile Maintenance", desc: "Ensure timely annual updates of IEC profile information as mandated by DGFT." },
              { number: "08", icon: "🎯", title: "Support", desc: "Assistance for IEC-related queries, amendments, and compliance management post-registration." }
            ].map((step, index) => (
              <div key={index} className="sm:col-span-2 lg:col-span-2 flex flex-col justify-between p-3 bg-white rounded-xl shadow-md text-center hover:shadow-lg transition-shadow border-t-4 border-blue-500">
                <div className="flex flex-col flex-grow justify-center">
                  <span className="text-xl font-extrabold text-blue-600 block leading-tight">{step.number}</span>
                  <span className="text-2xl mx-auto block leading-none">{step.icon}</span>
                  <h3 className="text-base font-semibold text-gray-900 leading-snug mt-0.5">{step.title}</h3>
                  <p className="text-gray-600 text-sm text-justify mt-1 leading-snug">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Only Scanned Copies are required
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Mandatory Documents for IEC Code Registration
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "📄", title: "Company Documents", desc: "Certificate of incorporation/ Memorandum of Association (MoA) / Articles of Association (AoA) / Copy of partnership deed, GST Certificate" },
              { icon: "🆔", title: "Identity Proof", desc: "PAN card (Company, Founders, Partners), Aadhaar Card & Passport size photographs of Directors / Founders" },
              { icon: "🏢", title: "Address Proof of Company", desc: "Business address proof (rent agreement, utility bill, etc.), Ownership papers of the registered office (if owned) or a rent/lease agreement and No Objection Certificate (NOC) from the landlord (if rented)." },
              { icon: "🔐", title: "Digital Signature Certificate (DSC) or Aadhaar OTP", desc: "For authentication while submitting the online application." },
              { icon: "🏦", title: "Bank Account Details", desc: "Cancelled cheque or bank certificate reflecting the name of the applicant or business entity." },
              { icon: "📝", title: "Board Resolution/Authorization Letter", desc: "Board resolution appointing authorised signatory / Any other proof of appointment of authorised signatory." }
            ].map((doc, index) => (
              <div key={index} className="p-6 border rounded-xl shadow-md flex items-start space-x-4 h-full">
                <span className="text-2xl flex-shrink-0 mt-1">{doc.icon}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
                  <p className="text-gray-600 text-sm mt-1 text-justify">{doc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section id="you-get" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Your Deliverables
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              What Akir Advisory Delivers
            </p>
          </div>

          {/* 5 equal boxes in a single row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: "📜", title: "Official IEC Certificate", desc: "Digital Import Export Code certificate issued by DGFT emailed to the applicant." },
              { icon: "🔢", title: "Unique IEC Number", desc: "A 10-digit IEC number which identifies the importing/exporting entity." },
              { icon: "📊", title: "Guidance for Profile Maintenance", desc: "Assistance with annual IEC profile updates required to keep the IEC active." },
              { icon: "🎯", title: "Post-Registration Support", desc: "Expert help for any amendments, compliance queries, or renewals related to IEC." },
              { icon: "💾", title: "Digital Copies and Record Keeping", desc: "Provision of electronic copies of all registration documents for your records." }
            ].map((item, index) => (
              <div key={index} className="p-5 bg-white rounded-xl shadow-lg border-l-4 border-green-500 flex flex-col h-full">
                <span className="text-2xl mb-2 block">{item.icon}</span>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-snug">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="service-faqs" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                  <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
                      Common Questions
                  </h2>
                  <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                      IEC FAQs
                  </p>
              </div>
              
              <div className="space-y-4 max-w-4xl mx-auto">
                  <div className="border rounded-lg p-5 shadow-sm">
                      <h4 className="font-semibold text-lg text-gray-900 mb-2">Is IEC registration mandatory for all traders?</h4>
                      <p className="text-gray-600">Yes, IEC is compulsory for all importers and exporters unless specifically exempted by DGFT.</p>
                  </div>
                  <div className="border rounded-lg p-5 shadow-sm">
                      <h4 className="font-semibold text-lg text-gray-900 mb-2">How long does IEC registration take?</h4>
                      <p className="text-gray-600">Usually 7-15 working days depending on document completeness and processing.</p>
                  </div>
                  <div className="border rounded-lg p-5 shadow-sm">
                      <h4 className="font-semibold text-lg text-gray-900 mb-2">Is Digital Signature Certificate (DSC) mandatory for IEC application?</h4>
                      <p className="text-gray-600">No, IEC can be applied using Aadhaar-based OTP authentication, but DSC strengthens security.</p>
                  </div>
                  <div className="border rounded-lg p-5 shadow-sm">
                      <h4 className="font-semibold text-lg text-gray-900 mb-2">Can a proprietor apply for IEC in his/her own name?</h4>
                      <p className="text-gray-600">Yes, individuals can apply either in their personal name or business entity name.</p>
                  </div>
              </div>
          </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-blue-700/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-white">
                Ready to IEC Code Registration?
            </h2>
            <p className="mt-4 text-xl text-blue-100 max-w-3xl mx-auto">
                Let our team of advisors simplify your IEC Code Registration journey.
            </p>
            <a href="#contact-section" className="mt-8 inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                <span className="mr-2">📞</span> Consult Our Expert Today
            </a>
        </div>
      </section>
    </main>
  );
};

export default IECCodeRegistration;